import { EventEmitter, Injectable } from '@angular/core';
import { WallSide } from '../enums/wallSide.enum';
import { ICoolingCell } from '../interfaces/coolingCell.interface';
import { IWall } from '../interfaces/wall.interface';
import { IDoorManager } from '../interfaces/doorManager.interface';
import { DoorDirection } from '../enums/doorDirection.enum';
import { DoorPosition } from '../models/doorPosition.model';
import { NotificationService } from './notification.service';
import { FinishTypes } from '../enums/finishTypes.enum';
import { ISceneManager, IWallMaterialPerSide } from '../interfaces/sceneManager.interface';
import { WallFinish } from '../models/wallFinish.model';
import { DoorType } from '../enums/doorType.enum';
import { IWallSelection } from '../interfaces/wallSelection.interface';
import { InnerWallType } from '../enums/innerWallType.enum';
import { InnerWall } from '../models/innerWall.model';
import { DoorHandleType } from '../enums/doorHandleType.enum';
import { HingeType } from '../enums/hingeType.enum';
import { BehaviorSubject } from 'rxjs';
import { Conflicts, InvalidPanel } from '../models/conflicts.model';

@Injectable({
  providedIn: 'root',
})
export class ThreeDService {
  doorAdded$: EventEmitter<any> = new EventEmitter<any>();
  doorReplaced$: EventEmitter<any> = new EventEmitter<any>();
  doorAddedFailed$: EventEmitter<any> = new EventEmitter<any>();
  doorCollision$: EventEmitter<any> = new EventEmitter<any>();
  doorDropsOffWall$: EventEmitter<any> = new EventEmitter<any>();
  doorDroppedOffWall$: EventEmitter<any> = new EventEmitter<any>();
  doorPositionSet$: EventEmitter<any> = new EventEmitter<any>();
  doorRemoved$: EventEmitter<any> = new EventEmitter<any>();
  updateDoors$: EventEmitter<any> = new EventEmitter<any>();
  doorSelected$: EventEmitter<DoorPosition> = new EventEmitter<DoorPosition>();
  doorSelectionRemoved$: EventEmitter<any> = new EventEmitter<any>();
  wallSelectionChanged$: EventEmitter<WallSide | number> = new EventEmitter<WallSide | number>();
  doorHeightChangedFailed$: EventEmitter<any> = new EventEmitter<any>();
  doorWidthChangedFailed$: EventEmitter<any> = new EventEmitter<any>();
  readyForInput$: EventEmitter<any> = new EventEmitter<any>();
  finishedBuilding$: EventEmitter<any> = new EventEmitter<any>();
  modelFinishedConstructing$: EventEmitter<number> = new EventEmitter<number>();
  conflicts$ = new BehaviorSubject<Conflicts>({ collisions: [], invalidPanels: [] } as Conflicts);
  readyReceived = false;
  readyReceived$ = new EventEmitter<boolean>();
  collided: boolean;
  doorDropsOffWall: boolean;
  nextCoolingCellCounter: number;
  private iframe: any = null;

  constructor(private notificationService: NotificationService) {}

  get wallSelectionEnabled(): boolean {
    if (!this.iframe || !this.readyReceived) {
      return null;
    }

    return this.iframe.isomasters3d.wallSelectionEnabled;
  }

  set wallSelectionEnabled(value: boolean) {
    if (!this.iframe || !this.readyReceived) {
      return;
    }

    this.iframe.isomasters3d.wallSelectionEnabled = value;
  }

  get innerWallSelectionEnabled(): boolean {
    if (!this.iframe || !this.readyReceived) {
      return null;
    }

    return this.iframe.isomasters3d.innerWallSelectionEnabled;
  }

  set innerWallSelectionEnabled(value: boolean) {
    if (!this.iframe || !this.readyReceived) {
      return;
    }

    this.iframe.isomasters3d.innerWallSelectionEnabled = value;
  }

  get doorSelectionEnabled(): boolean {
    if (!this.iframe || !this.readyReceived) {
      return null;
    }

    return this.iframe.isomasters3d.doorSelectionEnabled;
  }

  set doorSelectionEnabled(value: boolean) {
    if (!this.iframe || !this.readyReceived) {
      return;
    }

    this.doorManager.deselectDoor();
    this.iframe.isomasters3d.doorSelectionEnabled = value;
  }

  get ceilingSelectionEnabled(): boolean {
    if (!this.iframe || !this.readyReceived) {
      return null;
    }

    return this.iframe.isomasters3d.ceilingSelectionEnabled;
  }

  set ceilingSelectionEnabled(value: boolean) {
    if (!this.iframe || !this.readyReceived) {
      return;
    }

    this.doorManager.deselectDoor();
    this.iframe.isomasters3d.ceilingSelectionEnabled = value;
  }

  get modelIsReady(): boolean {
    if (!this.iframe || !this.readyReceived) {
      return false;
    }

    if (!this.coolingCell) {
      return false;
    }

    if (!this.coolingCell.readyForInput) {
      return false;
    }

    return true;
  }

  get selectedDoor(): DoorPosition {
    if (!this.iframe || !this.readyReceived) {
      return null;
    }
    return this.createDoorPosition(this.doorManager.getSelectedIDoor());
  }

  get doorManager(): IDoorManager {
    if (!this.iframe || !this.readyReceived) {
      return null;
    }
    return this.iframe.isomasters3d.getDoorManager();
  }

  get wallSelection(): IWallSelection {
    if (!this.iframe || !this.readyReceived) {
      return null;
    }
    return this.iframe.isomasters3d.getWallSelection();
  }

  get coolingCell(): ICoolingCell {
    if (!this.iframe || !this.readyReceived) {
      return null;
    }
    return this.iframe.isomasters3d.getCoolingCell();
  }

  setIframe(iframe: any): boolean {
    if (iframe && iframe.isomasters3d && iframe.isomasters3d.getCoolingCell()) {
      this.iframe = iframe;
      this.iframe.document.addEventListener('DoorSelected', this.doorSelectedEventHandler);
      this.iframe.document.addEventListener('DoorSelectionRemoved', this.doorSelectionRemovedEventHandler);
      this.iframe.document.addEventListener('ReadyForInput', this.readyInputEventHandler);
      this.iframe.document.addEventListener('DoorAdded', this.doorAddedEventHandler);
      this.iframe.document.addEventListener('DoorReplaced', this.doorReplacedEventHandler);
      this.iframe.document.addEventListener('DoorAddedFailed', this.doorAddedFailedEventHandler);
      this.iframe.document.addEventListener('DoorCollision', this.doorCollisionEventHandler);
      this.iframe.document.addEventListener('DoorDropsOffWall', this.doorDropsOffWallEventHandler);
      this.iframe.document.addEventListener('DoorPositionAdjusted', this.doorPositionAdjustedEventHandler);
      this.iframe.document.addEventListener('DoorRemoved', this.doorRemovedEventHandler);
      this.iframe.document.addEventListener('DoorDroppedOffWall', this.doorDroppedOffWallEventHandler);
      this.iframe.document.addEventListener('DoorHeightChanged', this.doorHeightChangedEventHandler);
      this.iframe.document.addEventListener('DoorPositionSet', this.doorPositionSetEventHandler);
      this.iframe.document.addEventListener('WallSelectionChanged', this.wallSelectionChangedEventHandler);
      this.iframe.document.addEventListener('DoorHeightDoesNotFit', this.doorHeightDoesNotFitEventHandler);
      this.iframe.document.addEventListener('DoorWidthDoesNotFit', this.doorWidthDoesNotFitEventHandler);
      this.iframe.document.addEventListener('DoorCantOpen', this.doorCantOpenEventHandler);
      this.iframe.document.addEventListener('NewInnerwallNotPossible', this.newInnerWallNotPossibleEventHandler);
      this.iframe.document.addEventListener('InnerwallRemoved', this.innerWallRemovedEventHandler);
      this.iframe.document.addEventListener('ReadyForMoreInput', () => {
        this.readyForInput$.emit();
      });
      this.iframe.document.addEventListener('FinishedBuilding', () => {
        this.finishedBuilding$.emit();
      });
      this.iframe.document.addEventListener('ModelFinishedConstructing', (event: CustomEvent<number>) => {
        this.modelFinishedConstructing$.emit(event.detail);
        this.updateConflicts();
      });

      const coolingCell = this.iframe.isomasters3d.getCoolingCell();
      if (coolingCell.readyForInput) {
        if (!this.readyReceived) {
          this.readyReceived = true;
          this.readyReceived$.emit(true);
        }
        return true;
      }
    }

    return false;
  }

  recreateCoolingCell(
    length: number,
    height: number,
    width: number,
    wallThickness: number,
    floorMaterial: string,
    floorVisible: boolean,
    ceilingMaterial: string,
    sunkenFloor: boolean,
    ventilationProfiles: boolean,
    plinths: number,
    moldingsEnabled: boolean,
    wallFinishes: WallFinish[],
    doors: DoorPosition[],
    innerWalls: InnerWall[] = [],
    coolingCellCounter?: number
  ) {
    if (!this.iframe || !this.readyReceived) {
      if (coolingCellCounter) {
        this.nextCoolingCellCounter = coolingCellCounter;
      }
      return null;
    }

    if (!coolingCellCounter && !!this.nextCoolingCellCounter) {
      coolingCellCounter = this.nextCoolingCellCounter;
      this.nextCoolingCellCounter = undefined;
    }

    const wallMaterialsPerSide = wallFinishes.map(
      wf =>
        ({
          side: wf.side,
          wallMaterials: {
            front: this.materialFromFinishType(wf.exterior),
            back: this.materialFromFinishType(wf.interior),
          },
        }) as IWallMaterialPerSide
    );
    (this.iframe.isomasters3d as ISceneManager).createCoolingCell(
      null,
      length,
      height,
      width,
      wallThickness,
      floorVisible,
      floorMaterial,
      ceilingMaterial,
      sunkenFloor,
      null,
      ventilationProfiles,
      null,
      plinths,
      null,
      moldingsEnabled,
      wallMaterialsPerSide,
      null,
      doors,
      null,
      innerWalls,
      coolingCellCounter
    );
  }

  doorPositionSetEventHandler = () => {
    this.doorPositionSet$.emit();
    this.collided = false;
    this.doorDropsOffWall = false;
  };

  doorPositionAdjustedEventHandler = () => {
    this.updateDoors$.emit();
  };

  doorCollisionEventHandler = () => {
    this.doorCollision$.emit();
    if (!this.collided) {
      this.collided = true;
      this.notificationService.showNotification('NOTIFICATIONS.DOOR.WOULDCOLLIDE', NotificationService.DEFAULT_TIMEOUT);
    }
  };

  doorDropsOffWallEventHandler = () => {
    this.doorDropsOffWall$.emit();
    if (!this.doorDropsOffWall) {
      this.doorDropsOffWall = true;
      this.notificationService.showNotification(
        'NOTIFICATIONS.DOOR.WOULDDROPOFFWALL',
        NotificationService.DEFAULT_TIMEOUT
      );
    }
  };

  doorDroppedOffWallEventHandler = () => {
    this.doorDroppedOffWall$.emit();
    this.notificationService.showNotification('NOTIFICATIONS.DOOR.DROPPEDOFFWALL', NotificationService.DEFAULT_TIMEOUT);
  };

  doorHeightChangedEventHandler = () => {
    this.notificationService.showNotification('NOTIFICATIONS.DOOR.HEIGHTCHANGED', NotificationService.DEFAULT_TIMEOUT);
    this.updateDoors$.emit();
  };

  doorSelectionRemovedEventHandler = () => {
    this.doorSelectionRemoved$.emit();
  };

  doorRemovedEventHandler = () => {
    this.doorRemoved$.emit();
  };

  doorAddedEventHandler = (door: CustomEvent) => {
    this.doorAdded$.emit(this.createDoorPosition(door.detail));
  };

  doorReplacedEventHandler = (door: CustomEvent) => {
    this.doorReplaced$.emit(this.createDoorPosition(door.detail));
  };

  doorAddedFailedEventHandler = () => {
    this.doorAddedFailed$.emit();
    this.notificationService.showNotification(
      'NOTIFICATIONS.DOOR.CANNOTBEADDEDHERE',
      NotificationService.DEFAULT_TIMEOUT
    );
  };

  readyInputEventHandler = () => {
    if (!this.readyReceived) {
      this.readyReceived = true;
      this.readyReceived$.emit(true);
      this.iframe.document.removeEventListener('ReadyForInput', this.readyInputEventHandler);
    }
  };

  doorSelectedEventHandler = () => {
    setTimeout(() => {
      this.doorSelected$.emit(this.createDoorPosition(this.selectedDoor));
    });
  };

  doorHeightDoesNotFitEventHandler = () => {
    this.doorHeightChangedFailed$.emit();
    this.notificationService.showNotification('NOTIFICATIONS.DOOR.TOOHIGH', NotificationService.DEFAULT_TIMEOUT);
  };

  doorWidthDoesNotFitEventHandler = () => {
    this.doorWidthChangedFailed$.emit();
    this.notificationService.showNotification('NOTIFICATIONS.DOOR.TOOWIDE', NotificationService.DEFAULT_TIMEOUT);
  };

  doorCantOpenEventHandler = () => {
    this.notificationService.showNotification('NOTIFICATIONS.DOOR.CANTOPEN', NotificationService.DEFAULT_TIMEOUT);
  };

  newInnerWallNotPossibleEventHandler = () => {
    this.notificationService.showNotification(
      'NOTIFICATIONS.INNERWALL.NEWNOTPOSSIBLE',
      NotificationService.DEFAULT_TIMEOUT
    );
  };

  innerWallRemovedEventHandler = () => {
    this.notificationService.showNotification('NOTIFICATIONS.INNERWALL.REMOVED', NotificationService.DEFAULT_TIMEOUT);
  };

  wallSelectionChangedEventHandler = (wallEvent: CustomEvent) => {
    if (wallEvent.detail >= 0) {
      this.wallSelectionChanged$.emit(wallEvent.detail as WallSide);
    } else if (wallEvent.detail === -1) {
      this.wallSelectionChanged$.emit(-1);
    }
  };

  getIframe(): any {
    return this.iframe;
  }

  setWallThickness(thickness: number): number | null {
    if (!this.iframe || !this.readyReceived) {
      return null;
    }
    return this.coolingCell.setWallThickness(thickness);
  }

  setHeight(valueInMeters: number): number | null {
    if (!this.iframe || !this.readyReceived) {
      return null;
    }
    return this.coolingCell.setHeight(valueInMeters);
  }

  setWidth(valueInMeters: number): number | null {
    if (!this.iframe || !this.readyReceived) {
      return null;
    }
    return this.coolingCell.setWidth(valueInMeters);
  }

  setLength(valueInMeters: number): number | null {
    if (!this.iframe || !this.readyReceived) {
      return null;
    }
    return this.coolingCell.setDepth(valueInMeters);
  }

  setWallsHidden(wallsHidden: boolean) {
    if (!this.iframe || !this.readyReceived) {
      return null;
    }
    return this.coolingCell.setHideWalls(wallsHidden);
  }

  setFloorMaterial(type: string) {
    if (!this.iframe || !this.readyReceived) {
      return null;
    }
    this.coolingCell.setFloorMaterial(type);
  }

  setDoorPosition(newDoorPosition: number) {
    if (!this.iframe || !this.readyReceived || !this.modelIsReady) {
      return null;
    }
    this.coolingCell.setSelectedDoorPosition(newDoorPosition);
  }

  removeSelectedDoor() {
    if (!this.iframe || !this.readyReceived) {
      return null;
    }
    this.coolingCell.removeSelectedDoor();
  }

  getWallBySide(side: WallSide, innerWallId?: string): IWall {
    if (!this.iframe || !this.readyReceived) {
      return null;
    }
    return this.coolingCell.getWallBySide(side, innerWallId);
  }

  getDoors(): DoorPosition[] {
    if (!this.iframe || !this.readyReceived) {
      return null;
    }

    const doors: DoorPosition[] = [];

    this.coolingCell.getDoors().forEach(d => {
      doors.push(this.createDoorPosition(d));
    });

    return doors;
  }

  createDoorPosition(d: DoorPosition): DoorPosition {
    if (d === undefined) {
      return d;
    }

    return new DoorPosition(
      d.id,
      d.left,
      d.side,
      d.doorModel.openingWidth,
      d.doorModel.openingHeight,
      d.doorModel.panelWidth,
      d.doorModel.panelHeight,
      d.frameInteriorFinish,
      d.frameExteriorFinish,
      d.leafInteriorFinish,
      d.leafExteriorFinish,
      d.doorModel.type,
      d.direction,
      d.hingeType,
      d.doorHandleType,
      d.innerWallId
    );
  }

  setDoors(doors: DoorPosition[]) {
    if (!this.iframe || !this.readyReceived) {
      return null;
    }

    this.coolingCell.setDoorsByUsefullDimensions(doors);
  }

  getDoorsBySide(side: WallSide): DoorPosition[] {
    const doors = this.getDoors();
    if (doors) {
      return doors.filter(d => d.side === side);
    } else {
      return [];
    }
  }

  setDoorRotationSide(side: DoorDirection) {
    if (!this.iframe) {
      return null;
    }
    this.coolingCell.setDoorRotationSide(side);
  }

  setDoorSlidingDirection(direction: DoorDirection) {
    if (!this.iframe) {
      return null;
    }
    this.coolingCell.setDoorSlidingDirection(direction);
  }

  setVentilationProfiles(value: boolean) {
    if (!this.iframe || !this.readyReceived) {
      return null;
    }
    this.coolingCell.setVentilationProfiles(value);
  }

  setSunkenFloor(value: boolean) {
    if (!this.iframe || !this.readyReceived) {
      return null;
    }
    this.coolingCell.setSunkenFloor(value);
  }

  setPlinthsOnSide(plinths: number) {
    if (!this.iframe || !this.readyReceived) {
      return null;
    }
    if (plinths < 0 || plinths > 3) {
      return null;
    }
    this.coolingCell.setPlinthsOnSide(plinths);
  }

  setCeilingMoldings(value: boolean) {
    if (!this.iframe || !this.readyReceived) {
      return null;
    }
    this.coolingCell.setCeilingMoldings(value);
  }

  async createAllScreenshots(): Promise<void> {
    if (!this.iframe || !this.readyReceived) {
      return null;
    }
    this.iframe.isomasters3d.getCameraControls().repositionCameraTarget();
    await this.iframe.isomasters3d.createAllScreenshots();
  }

  getMainScreenshot(big?: boolean, inside?: boolean): string {
    if (!this.iframe || !this.readyReceived) {
      return null;
    }
    return this.iframe.isomasters3d.createMainScreenshot(big, inside);
  }

  getScreenshotsForSidesWithDoors() {
    if (!this.iframe || !this.readyReceived) {
      return null;
    }
    this.setColoringOfCollision(false);
    this.iframe.isomasters3d.getCameraControls().repositionCameraTarget();
    const screenShots = this.iframe.isomasters3d.createScreenshotsForSidesWithDoors();
    this.setColoringOfCollision(true);
    return screenShots;
  }

  setFloorVisible(visible: boolean) {
    if (!this.iframe || !this.readyReceived) {
      return null;
    }
    return this.coolingCell.setFloorVisible(visible);
  }

  getFloorVisible(): boolean | null {
    if (!this.iframe || !this.readyReceived) {
      return null;
    }
    return this.coolingCell.getFloorVisible();
  }

  isValidDoorPosition(newLeftPosition: number): boolean {
    if (!this.iframe || !this.readyReceived) {
      return false;
    }
    return this.coolingCell.isValidSelectedDoorPosition(newLeftPosition);
  }

  isValidDoorWidth(newDoorWidth: number): boolean {
    if (!this.iframe || !this.readyReceived) {
      return false;
    }
    return this.coolingCell.isValidSelectedDoorWidth(newDoorWidth);
  }

  isValidDoorHeight(newDoorHeight: number): boolean {
    if (!this.iframe || !this.readyReceived) {
      return false;
    }
    return this.coolingCell.isValidSelectedDoorHeight(newDoorHeight);
  }

  updateWallFinish(wallSide: WallSide, outside: boolean, finishType: FinishTypes, innerWallId?: string) {
    if (!this.iframe || !this.readyReceived) {
      return null;
    }
    if (wallSide === WallSide.floor) {
      return;
    }
    if (wallSide === WallSide.ceiling) {
      if (outside) {
        this.coolingCell.setCeilingColor(this.materialFromFinishType(finishType));
      } else {
        this.coolingCell.setCeilingColor(this.materialFromFinishType(finishType), true);
      }
      return;
    }
    const wall = this.coolingCell.getWallBySide(wallSide, innerWallId);
    const wallMaterial = this.materialFromFinishType(finishType);
    this.coolingCell.setWallMaterial(wall, wallMaterial, outside, wallSide === WallSide.inner ? finishType : undefined);
  }

  materialFromFinishType(finishType: FinishTypes): string {
    switch (finishType) {
      case FinishTypes.STEEL:
      case FinishTypes.STEEL_PET55:
        return '#FFFFFF';
      case FinishTypes.RVS304_ROUND_BRUSH:
      case FinishTypes.RVS316_ROUND_BRUSH:
        return 'rvs-circular';
      case FinishTypes.RVS304_DESIGN_5WL:
      case FinishTypes.RVS304_LINEN:
        return 'rvs-plate';
      case FinishTypes.RVS304_COLLED_ROLLED:
      case FinishTypes.RVS304_LINE_BRUSH:
        return 'rvs';
    }
  }

  setCameraOnSide(side: WallSide) {
    this.doorManager.setCameraOnSide(side);
  }

  addNewDoor(
    type: DoorType,
    side: WallSide,
    direction: DoorDirection,
    width: number,
    height: number,
    left: number,
    materials: string[]
  ) {
    if (!this.iframe || !this.readyReceived) {
      return null;
    }
    let innerWallId = null;
    if (side === WallSide.inner) {
      const wall = this.wallSelection.getSelectedWall();
      if (wall.innerWall) {
        innerWallId = wall.innerWall.id;
      }
    }
    this.coolingCell.addNewDoor(
      type,
      side,
      direction,
      width,
      height,
      left,
      false,
      materials,
      undefined,
      undefined,
      null,
      innerWallId
    );
  }

  selectFirstDoor() {
    if (!this.iframe || !this.readyReceived) {
      return null;
    }
    this.doorManager.selectFirstDoor();
  }

  setSelectedDoorHeight(value: number) {
    if (!this.iframe || !this.readyReceived || !this.modelIsReady) {
      return null;
    }

    this.coolingCell.setSelectedDoorHeight(value);
  }

  setSelectedDoorWidth(value: number) {
    if (!this.iframe || !this.readyReceived || !this.modelIsReady) {
      return null;
    }

    return this.coolingCell.setSelectedDoorWidth(value);
  }

  updateSelectedDoorFinish(outside: boolean, finish: FinishTypes) {
    if (!this.iframe || !this.readyReceived) {
      return null;
    }
    if (outside) {
      this.coolingCell.setSelectedDoorPanelOutsideMaterial(this.materialFromFinishType(finish));
    } else {
      this.coolingCell.setSelectedDoorPanelInsideMaterial(this.materialFromFinishType(finish));
    }
  }

  deselect() {
    if (!this.iframe || !this.readyReceived) {
      return null;
    }

    this.coolingCell.deselectAll();
  }

  setZoom(zoom: number) {
    if (!this.iframe || !this.readyReceived) {
      return null;
    }

    this.iframe.isomasters3d.getCameraControls().setCameraDistance(false, zoom, true);
  }

  resetDefaultCamera(low = false) {
    if (!this.iframe || !this.readyReceived) {
      return null;
    }
    if (low) {
      this.iframe.isomasters3d.getCameraControls().addToCameraMoveList('standardlow');
      return;
    }
    this.iframe.isomasters3d.getCameraControls().addToCameraMoveList('standard');
  }

  removeDoorsOnSide(wallSide: WallSide, prepareCoolingCell = true) {
    if (!this.iframe || !this.readyReceived) {
      return null;
    }
    this.coolingCell.removeDoorsOnSide(wallSide, prepareCoolingCell);
  }

  removeDoorsOnInnerWalls(prepareCoolingCell = true) {
    if (!this.iframe || !this.readyReceived) {
      return null;
    }
    this.coolingCell.removeDoorsOnInnerWalls(prepareCoolingCell);
  }

  addInnerWall(innerWallType: InnerWallType, left?: number): string {
    if (!this.iframe || !this.readyReceived) {
      return null;
    }
    return this.coolingCell.addInnerWall(innerWallType, left);
  }

  getInnerWalls(): InnerWall[] {
    if (!this.iframe || !this.readyReceived) {
      return null;
    }
    return this.coolingCell.getInnerWalls();
  }

  setInnerWallPosition(newPosition: number): void {
    if (!this.iframe || !this.readyReceived) {
      return null;
    }
    this.coolingCell.setInnerWallPosition(newPosition);
  }

  removeSelectedInnerWall() {
    if (!this.iframe || !this.readyReceived) {
      return null;
    }
    this.coolingCell.removeSelectedInnerWall();
    this.updateDoors$.emit();
  }

  removeInnerWall(iw: InnerWall) {
    if (!this.iframe || !this.readyReceived) {
      return null;
    }
    this.coolingCell.removeInnerWall(iw);
    this.updateDoors$.emit();
  }

  flipInnerWall(innerWall: InnerWall) {
    if (!this.iframe || !this.readyReceived) {
      return null;
    }
    this.coolingCell.flipInnerWall(innerWall);
  }

  changeSelectedDoorHandle(doorHandleType: DoorHandleType) {
    if (!this.iframe || !this.readyReceived) {
      return null;
    }
    this.coolingCell.changeSelectedDoorHandle(doorHandleType);
  }

  changeDoorHandle(door: DoorPosition, doorHandleType: DoorHandleType) {
    if (!this.iframe || !this.readyReceived) {
      return null;
    }
    this.coolingCell.changeDoorHandle(door, doorHandleType);
  }

  changeSelectedDoorHinge(doorHingeType: HingeType) {
    if (!this.iframe || !this.readyReceived) {
      return null;
    }
    this.coolingCell.changeSelectedDoorHinge(doorHingeType);
  }

  doesDoorHeightFit(type: DoorType, height: number): boolean {
    if (!this.iframe || !this.readyReceived) {
      return null;
    }
    return this.coolingCell.doesDoorHeightFit(type, height);
  }

  getConflicts(): Conflicts {
    if (!this.iframe || !this.readyReceived) {
      return null;
    }
    const conflicts = this.coolingCell.getConflicts();
    const list: InvalidPanel[] = [];
    conflicts.invalidPanels.forEach(ip => {
      if (!list.some(w => w.size == ip.size)) {
        list.push({ size: ip.size, count: 0 });
      }
      list.find(w => w.size === ip.size).count++;
    });
    conflicts.invalidPanels = list;
    return conflicts;
  }

  setColoringOfCollision(on: boolean) {
    if (!this.iframe || !this.readyReceived) {
      return null;
    }
    this.coolingCell.setColoringOfCollision(on);
  }

  private updateConflicts() {
    this.conflicts$.next(this.getConflicts());
  }
}
